"use client"
import * as React from "react"
import dynamic from "next/dynamic"
import HeroCarousel from "./HeroCarousel"

// Background effect components (uncomment the one you want to use)
const Orb = dynamic(() => import("@/components/Orb"), { ssr: false })
const DotGrid = dynamic(() => import("@/components/DotGrid"), { ssr: false })
const Hyperspeed = dynamic(() => import("@/components/Hyperspeed"), { ssr: false })

// Check if WebGL is available
function isWebGLAvailable(): boolean {
  if (typeof window === "undefined") return false
  try {
    const canvas = document.createElement("canvas")
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    )
  } catch {
    return false
  }
}

const Hero: React.FC = () => {
  const [webglSupported, setWebglSupported] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(true)

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    setWebglSupported(isWebGLAvailable())
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <React.Suspense fallback={<div className="min-h-[70vh] md:min-h-screen w-full animate-pulse bg-muted rounded-3xl" />}>
      <div className={`relative pt-4 md:pt-4 pb-4 md:pb-10 ${isMobile ? 'min-h-[70vh]' : 'min-h-screen'}`}>
        {/* Background Effects Container - hidden on mobile for performance */}
        {!isMobile && (
          <div className="absolute inset-0 z-0 opacity-70 pointer-events-none overflow-hidden" style={{ height: '100%', width: '100%' }}>

            {/* Option 1: Orb Background */}
            {/*<Orb
            hoverIntensity={0}
            rotateOnHover={false}
            hue={180}
            forceHoverState={false}
            backgroundColor="transparent"
          />*/}

            {/* Option 2: DotGrid Background */}
            {!webglSupported && <DotGrid
              dotSize={4}
              gap={15}
              baseColor="#1c8fad"
              activeColor="#1c8fad"
              proximity={120}
              shockRadius={250}
              shockStrength={5}
              resistance={750}
              returnDuration={1.5}
            />}

            {/* Option 3: Hyperspeed Background (active) - only if WebGL is supported */}
            {webglSupported && (
              <Hyperspeed
                effectOptions={{
                  onSpeedUp: () => { },
                  onSlowDown: () => { },
                  distortion: 'turbulentDistortion',
                  length: 400,
                  roadWidth: 10,
                  islandWidth: 2,
                  lanesPerRoad: 4,
                  fov: 90,
                  fovSpeedUp: 150,
                  speedUp: 2,
                  carLightsFade: 0.4,
                  totalSideLightSticks: 20,
                  lightPairsPerRoadWay: 40,
                  shoulderLinesWidthPercentage: 0.05,
                  brokenLinesWidthPercentage: 0.1,
                  brokenLinesLengthPercentage: 0.5,
                  lightStickWidth: [0.12, 0.5],
                  lightStickHeight: [1.3, 1.7],
                  movingAwaySpeed: [60, 80],
                  movingCloserSpeed: [-120, -160],
                  carLightsLength: [400 * 0.03, 400 * 0.2],
                  carLightsRadius: [0.05, 0.14],
                  carWidthPercentage: [0.3, 0.5],
                  carShiftX: [-0.8, 0.8],
                  carFloorSeparation: [0, 5],
                  colors: {
                    roadColor: 0x080808,
                    islandColor: 0x0a0a0a,
                    background: 0x000000,
                    shoulderLines: 0xFFFFFF,
                    brokenLines: 0xFFFFFF,
                    leftCars: [0xD856BF, 0x6750A2, 0xC247AC],
                    rightCars: [0x03B3C3, 0x0E5EA5, 0x324555],
                    sticks: 0x03B3C3,
                  }
                }}
              />
            )}

          </div>
        )}
        {/* Content on top of background effect */}
        <div className="relative z-10">
          <HeroCarousel />
        </div>
      </div>
    </React.Suspense>
  )
}

export default Hero
